// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import StyledBox, { StyledBoxStyles } from "@keller/App/Components/Common/StyledBox";
import { styledScrollbar, xScreen } from "@keller/XDL/Utilities";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showAddLink = "showAddLink",
    link = "link",
}

interface DashboardRowProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showAddLink]: boolean;
        [fields.link]: LinkFieldValue;
    };
}

type Props = DashboardRowProps;

interface DashboardRowStyles {
    styledBox?: StyledBoxStyles;
    link?: LinkPresentationProps;
    title?: TypographyProps;
}

const dashboardRowStyles: DashboardRowStyles = {
    styledBox: {
        container: {
            css: css`
                margin-bottom: 48px;
                ${xScreen("lte", "md")} {
                    max-height: 400px;
                    ${styledScrollbar(5)}
                }
            `,
        },
    },
    title: {
        variant: "h4",
        size: 24,
        weight: 900,
        css: css`
            margin-bottom: 24px;
            & > ${TypographyStyle} {
                margin-bottom: 0;
            }
        `,
    },
};

dashboardRowStyles.link = {
    color: "text.main",
    typographyProps: {
        ...dashboardRowStyles.title,
        css: css``,
    },
    css: css`
        ${dashboardRowStyles.title?.css}
    `,
};

const styles = dashboardRowStyles;

const DashboardRow: FC<Props> = ({ fields: { title, link: fieldsLink }, id }) => {
    const link = useGetLink(fieldsLink);

    const titleElement = link.url ? (
        <Link href={link.url} {...styles.link}>
            {title}
        </Link>
    ) : (
        <Typography {...styles.title}>{title}</Typography>
    );

    return (
        <StyledBox extendedStyles={styles.styledBox} data-test-selector="dashboard-widget-wrapper">
            {title && titleElement}
            <Zone contentId={id} zoneName="Content" />
        </StyledBox>
    );
};
const widgetModule: WidgetModule = {
    component: DashboardRow,
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.showAddLink,
                displayName: "Add link",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                variant: "toggle",
            },
            {
                name: fields.link,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Page" },
                isRequired: true,
                fieldType: "General",
                allowCategories: item => false,
                allowUrls: item => false,
                isVisible: item => item.fields.showAddLink,
            },
        ],
    },
};

export default widgetModule;
